/* eslint-disable no-unused-vars,max-len */
/* global G */
import { curry } from 'lib/util'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { hidden } from 'lib/sequence/component/state/hidden'
import { disabled } from 'lib/sequence/component/state/disabled'

/**
 * A static list of field types that are allowed to be taken into consideration
 * for focusing.
 *
 * @type {string[]}
 */
const allowedFieldTypes = [
  'text',
  // 'autocomplete', // TODO: See GAIA-1414
  // 'date', // TODO: See GAIA-1414
  // 'time', // TODO: See GAIA-1414
]

/**
 * Predicate function that checks if `field` can be focused.
 *
 * @param {Gaia.Component.Spec} field the field to check
 * @returns {boolean}
 */
const canBeFocused = field => allowedFieldTypes.find(type => field[G.CONFIGURATION]?._name?.includes?.(type))
  && !hidden(field)
  && !disabled(field)

/**
 * Recursive function that walks down the component tree and finds all fields for which
 * {@link canBeFocused} returned `true`. Will collect all fields in an array and
 * returns it.
 *
 * @param {Gaia.AppModule.Spec} module        app module
 * @param {Gaia.Component.Spec} component     action component
 * @param {Gaia.AppModule.EventHandler} event the event that triggered this handler
 * @returns {*[]}
 */
const findFocusableFields = (module, component, event) => {
  const { field, result = [] } = event?.detail || event || {}

  canBeFocused(field)
    ? result.push(field)
    : (field[G.CHILDREN] || []).forEach((child) => {
      findFocusableFields(module, component, { detail: { field: child, result } })
    })

  return result
}

/**
 * Focus Next Event Handler
 *
 * Will determine the next field that is below `root` (or the action component) in the component
 * tree that can be focused and will do so. If no field could be found it'll focus the current
 * field again.
 *
 * @param {string} [root]                     name of the up-most component to look for fields.
 *                                            If not defined, the action component will be used.
 * @param {Gaia.AppModule.Spec} module        app module
 * @param {Gaia.Component.Spec} component     action component
 * @param {Gaia.AppModule.EventHandler} event the event that triggered this handler
 */
const focusNext = (root, module, component, event) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { [root]: rootComponent } = sequenceComponentFindProxy(actionComponent)
  const parent = root ? rootComponent : actionComponent

  const fields = findFocusableFields(module, component, { detail: { field: parent } })
  const currentFieldIndex = fields.findIndex(field => field._name === component._name)
  const nextFieldIndex = currentFieldIndex === -1 || currentFieldIndex + 1 >= fields.length
    ? currentFieldIndex
    : currentFieldIndex + 1

  const nextField = fields?.[nextFieldIndex]

  if (nextField) {
    nextField[G.STATE].focus = {}
    module[G.ADAPTER][G.UI].update(module)
  }
}

export default curry(focusNext)
